/**
 * checklistDefinitions.js
 *
 * SIMPLIFIED CHECKLISTS - 2025-01-08
 *
 * Defines the checklists available in each category.
 * These are VERIFICATION checklists for qualified skippers.
 *
 * Equipment inspections are handled by the Maintenance module.
 *
 * CHANGES:
 * - Removed Tier 1/Tier 2 system (users customise instead)
 * - Removed phase headers (single flat lists)
 * - Reduced from 160+ items to ~53 items
 * - Focus on VERIFICATION not PROCEDURE
 */

// FT-126: Removed Ship, MapPin icons (no longer needed after removing voyage phase checklists)
import {
  Anchor,
  CloudRain,
  Eye,
  Users,
  Fuel,
  RefreshCw,
  ArrowRightLeft,
  Clipboard,
  UserPlus,
  Navigation,
  Moon,
  ArrowUpFromLine,
  Flame,
  Waves
} from 'lucide-react';

// Import emergency checklists
import { EMERGENCY_CHECKLISTS as EMERGENCY_CONFIG } from '../../config/emergencyChecklists';

// Import simplified checklists
// FT-126: Removed PRE_DEPARTURE_CHECKLIST, PRE_ARRIVAL_CHECKLIST, ARRIVAL_CHECKLIST
// These voyage phase checklists are now managed ONLY via Voyage Settings
import {
  TOWING_CHECKLIST,
  PERSONNEL_TRANSFER_CHECKLIST,
  BUNKERING_CHECKLIST,
  ANCHORING_CHECKLIST,
  RESTRICTED_VISIBILITY_CHECKLIST,
  HEAVY_WEATHER_CHECKLIST,
  WATCH_HANDOVER_CHECKLIST,
  PILOT_TRANSFER_CHECKLIST,
  CONFINED_WATERS_CHECKLIST,
  NIGHT_PASSAGE_CHECKLIST,
  LIFTING_OPERATIONS_CHECKLIST,
  HOT_WORK_CHECKLIST,
  DIVING_SUPPORT_CHECKLIST,
  SIMPLIFIED_CHECKLISTS
} from './checklists';

// ============================================================================
// NAVIGATION CATEGORY (8 simplified checklists)
// ============================================================================
// FT-126 FIX: Removed pre_departure, pre_arrival, arrival from NAVIGATION_CHECKLISTS
// These are now managed ONLY via Voyage Settings, not via Checklists editor
export const NAVIGATION_CHECKLISTS = [
  {
    id: 'watch_handover',
    name: WATCH_HANDOVER_CHECKLIST.name,
    description: WATCH_HANDOVER_CHECKLIST.description,
    icon: RefreshCw,
    isBuiltIn: true,
    items: WATCH_HANDOVER_CHECKLIST.items,
    regulatory: WATCH_HANDOVER_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'heavy_weather',
    name: HEAVY_WEATHER_CHECKLIST.name,
    description: HEAVY_WEATHER_CHECKLIST.description,
    icon: CloudRain,
    isBuiltIn: true,
    items: HEAVY_WEATHER_CHECKLIST.items,
    regulatory: HEAVY_WEATHER_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'restricted_visibility',
    name: RESTRICTED_VISIBILITY_CHECKLIST.name,
    description: RESTRICTED_VISIBILITY_CHECKLIST.description,
    icon: Eye,
    isBuiltIn: true,
    items: RESTRICTED_VISIBILITY_CHECKLIST.items,
    regulatory: RESTRICTED_VISIBILITY_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'confined_waters',
    name: CONFINED_WATERS_CHECKLIST.name,
    description: CONFINED_WATERS_CHECKLIST.description,
    icon: Navigation,
    isBuiltIn: true,
    items: CONFINED_WATERS_CHECKLIST.items,
    regulatory: CONFINED_WATERS_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'night_passage',
    name: NIGHT_PASSAGE_CHECKLIST.name,
    description: NIGHT_PASSAGE_CHECKLIST.description,
    icon: Moon,
    isBuiltIn: true,
    items: NIGHT_PASSAGE_CHECKLIST.items,
    regulatory: NIGHT_PASSAGE_CHECKLIST.regulatory,
    isCustomisable: true
  }
];

// ============================================================================
// OPERATIONAL CATEGORY (8 simplified checklists)
// ============================================================================
export const OPERATIONAL_CHECKLISTS = [
  {
    id: 'anchoring',
    name: ANCHORING_CHECKLIST.name,
    description: ANCHORING_CHECKLIST.description,
    icon: Anchor,
    isBuiltIn: true,
    items: ANCHORING_CHECKLIST.items,
    regulatory: ANCHORING_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'towing',
    name: TOWING_CHECKLIST.name,
    description: TOWING_CHECKLIST.description,
    icon: ArrowRightLeft,
    isBuiltIn: true,
    items: TOWING_CHECKLIST.items,
    regulatory: TOWING_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'personnel_transfer',
    name: PERSONNEL_TRANSFER_CHECKLIST.name,
    description: PERSONNEL_TRANSFER_CHECKLIST.description,
    icon: Users,
    isBuiltIn: true,
    items: PERSONNEL_TRANSFER_CHECKLIST.items,
    regulatory: PERSONNEL_TRANSFER_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'pilot_transfer',
    name: PILOT_TRANSFER_CHECKLIST.name,
    description: PILOT_TRANSFER_CHECKLIST.description,
    icon: UserPlus,
    isBuiltIn: true,
    items: PILOT_TRANSFER_CHECKLIST.items,
    regulatory: PILOT_TRANSFER_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'bunkering',
    name: BUNKERING_CHECKLIST.name,
    description: BUNKERING_CHECKLIST.description,
    icon: Fuel,
    isBuiltIn: true,
    items: BUNKERING_CHECKLIST.items,
    regulatory: BUNKERING_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'lifting_operations',
    name: LIFTING_OPERATIONS_CHECKLIST.name,
    description: LIFTING_OPERATIONS_CHECKLIST.description,
    icon: ArrowUpFromLine,
    isBuiltIn: true,
    items: LIFTING_OPERATIONS_CHECKLIST.items,
    regulatory: LIFTING_OPERATIONS_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'hot_work',
    name: HOT_WORK_CHECKLIST.name,
    description: HOT_WORK_CHECKLIST.description,
    icon: Flame,
    isBuiltIn: true,
    items: HOT_WORK_CHECKLIST.items,
    regulatory: HOT_WORK_CHECKLIST.regulatory,
    isCustomisable: true
  },
  {
    id: 'diving_support',
    name: DIVING_SUPPORT_CHECKLIST.name,
    description: DIVING_SUPPORT_CHECKLIST.description,
    icon: Waves,
    isBuiltIn: true,
    items: DIVING_SUPPORT_CHECKLIST.items,
    regulatory: DIVING_SUPPORT_CHECKLIST.regulatory,
    isCustomisable: true
  }
];

// ============================================================================
// EMERGENCY CATEGORY (from emergencyChecklists.js)
// ============================================================================
export const EMERGENCY_CHECKLISTS = EMERGENCY_CONFIG.checklists.map(checklist => ({
  id: checklist.id,
  name: checklist.title,
  description: 'Emergency response procedure',
  icon: checklist.icon,
  isBuiltIn: true,
  isEmergency: true,
  items: checklist.steps.map((step, idx) => ({
    id: `${checklist.id}_${idx}`,
    text: step.text,
    required: step.required !== false
  })),
  regulatory: checklist.reference || 'Emergency Procedures',
  duration: checklist.duration
}));

// ============================================================================
// FAMILIARISATION CATEGORY (1 checklist - links to existing UI)
// ============================================================================
export const FAMILIARISATION_CHECKLISTS = [
  {
    id: 'new_crew_induction',
    name: 'New Crew Induction',
    description: 'View in Crew → Familiarisation tab',
    icon: Users,
    isBuiltIn: true,
    hasExistingUI: true,
    linkTo: 'familiarisation',
    regulatory: 'WBC3 7.2'
  }
];

// ============================================================================
// REMOVED CATEGORIES
// ============================================================================
// The following have been REMOVED because they duplicate the Maintenance module:
// - DAILY_CHECKLISTS (equipment checks → use Maintenance)
// - PERIODIC_CHECKLISTS (weekly/monthly → use Maintenance)
// - PERIODIC_INSPECTION_CHECKLISTS (equipment inspections → use Maintenance)
//
// If any component imports these, provide empty arrays:
export const DAILY_CHECKLISTS = [];
export const PERIODIC_CHECKLISTS = [];
export const PERIODIC_INSPECTION_CHECKLISTS = [];

// ============================================================================
// CATEGORY TO CHECKLISTS MAP
// ============================================================================
export const CATEGORY_CHECKLISTS = {
  navigation: NAVIGATION_CHECKLISTS,
  operational: OPERATIONAL_CHECKLISTS,
  familiarisation: FAMILIARISATION_CHECKLISTS,
  emergency: EMERGENCY_CHECKLISTS
};

/**
 * Get checklists for a category
 */
export const getChecklistsForCategory = (categoryId, customChecklists = []) => {
  const builtIn = CATEGORY_CHECKLISTS[categoryId] || [];
  const custom = customChecklists.filter(c => c.category === categoryId);
  return [...builtIn, ...custom];
};

/**
 * Get all simplified checklists
 */
export const getAllSimplifiedChecklists = () => {
  return SIMPLIFIED_CHECKLISTS;
};

/**
 * Get a specific checklist by ID
 */
export const getChecklistById = (checklistId) => {
  return SIMPLIFIED_CHECKLISTS[checklistId] || null;
};

/**
 * Legacy compatibility - getChecklistForTier
 * Since we removed tiers, this just returns the items directly
 */
export const getChecklistForTier = (checklistId, tier = 1) => {
  const checklist = SIMPLIFIED_CHECKLISTS[checklistId];
  return checklist ? checklist.items : [];
};
